import { Package, X, Clock, CheckCircle, PaperPlaneTilt } from '@phosphor-icons/react';

export const API = `${process.env.REACT_APP_BACKEND_URL}/api/shizai`;
export const NONE = '__none__';
export const newKey = () => Math.random().toString(36).slice(2);

// iter_151 · 部品受注入力は「マスター検索のみ」に変更（2026-02 per user）。
// 新規品目の直接入力は廃止。品目行は マスターから引き当てた item_id + 数量 のみが必須。
// name/part_no/maker 等は表示用に UI 側で持つが、送信時は item_id で解決する。
export const emptyLine = () => ({
  _key: newKey(),
  item_id: '',
  item_source: '',
  // display-only snapshot fetched from master (read-only in UI)
  name: '', part_no: '', drawing_no: '', maker: '', maker_pn: '', model_no: '', material: '',
  qty: 1, uom: '個',
  reference_url: '',
  notes: '',
  // 手配区分（2026-06）: purchase=購入品（REQ → TECHS）/ in_house=自社製作（技術の製作依頼）
  make_type: 'purchase',
  // 自社製作のときの納入先（受け取る部署の台帳＋場所）
  deliver_register: '', deliver_location_id: '', deliver_location_name: '',
});

export const emptyForm = () => ({
  needed_by: '',
  priority: 'normal',
  order_name: '',
  order_category_id: '',
  delivery_destination: null,
  mfg_order_id: '',
  is_tax_inclusive: false,
  notes: '',
  lines: [emptyLine()],
});

// A line the user hasn't touched at all (no master picked, no notes).
export const isBlankLine = (ln) => !ln.item_id && !(ln.notes || '').trim();

// Per-line validation — every non-blank line MUST have a picked master item + qty ≥ 1.
export const validateLines = (lines) => {
  const active = lines.filter((ln) => !isBlankLine(ln));
  if (active.length === 0) return { error: '少なくとも1件の品目を登録マスターから選択してください' };
  for (const ln of active) {
    const no = lines.indexOf(ln) + 1;
    if (!ln.item_id) return { error: `品目 ${no}: 登録マスターから品目を選択してください` };
    if (!(Number(ln.qty) > 0)) return { error: `品目 ${no}: 数量は1以上を入力してください` };
    if (ln.make_type === 'in_house' && !ln.deliver_register) {
      return { error: `品目 ${no}: 自社製作の行は納入先の部署を選んでください` };
    }
  }
  return { lines: active };
};

// 全部 自社製作 の依頼は REQ を作らない（買う物が無い）。お客様向けの
// 受注品名 / 分類 / 納入先 は要らないので聞かない。
export const isAllInHouse = (lines) => {
  const active = (lines || []).filter((ln) => !isBlankLine(ln));
  return active.length > 0 && active.every((ln) => ln.make_type === 'in_house');
};

// Header-level required fields — 受注品名 / 分類 / 納期 / 納入先.
export const validateHeader = (form, allInHouse = false) => {
  const errors = {};
  if (!form.needed_by) errors.needed_by = '希望納期を入力してください';
  if (allInHouse) return errors;
  if (!(form.order_name || '').trim()) errors.order_name = '受注品名を入力してください';
  if (!form.order_category_id) errors.order_category_id = '分類を選択してください';
  if (!form.delivery_destination?.id) errors.delivery_destination = '納入先を選択してください';
  return errors;
};

export const toLineBody = (ln) => ({
  item_id: ln.item_id || null,
  item_source: ln.item_source || null,
  part_no: ln.part_no || null,
  drawing_no: ln.drawing_no || null,
  name: ln.name || null,
  maker: ln.maker || null,
  maker_pn: ln.maker_pn || null,
  model_no: ln.model_no || null,
  qty: Number(ln.qty),
  uom: ln.uom || '個',
  reference_url: ln.reference_url || null,
  // attachment fields are no longer collected on this screen
  photo_url: null,
  photo_is_pdf: false,
  photo_filename: null,
  notes: ln.notes || null,
  make_type: ln.make_type === 'in_house' ? 'in_house' : 'purchase',
  deliver_register: ln.deliver_register || null,
  deliver_location_id: ln.deliver_location_id || null,
  deliver_location_name: ln.deliver_location_name || null,
});

export const PRIORITY_META = {
  low:    { label: '低',   badge: 'bg-zinc-100 text-zinc-600 border-zinc-200' },
  normal: { label: '通常', badge: 'bg-sky-50 text-sky-700 border-sky-200' },
  high:   { label: '高',   badge: 'bg-rose-50 text-rose-700 border-rose-200' },
};

export const STATUS_META = {
  open:      { label: '未処理', badge: 'bg-amber-50 text-amber-700 border-amber-200', Icon: Clock },
  ordered:   { label: '発注済', badge: 'bg-sky-50 text-sky-700 border-sky-200',       Icon: PaperPlaneTilt },
  partial:   { label: '一部入荷', badge: 'bg-indigo-50 text-indigo-700 border-indigo-200', Icon: Package },
  received:  { label: '入荷済', badge: 'bg-emerald-50 text-emerald-700 border-emerald-200', Icon: CheckCircle },
  cancelled: { label: '取消', badge: 'bg-zinc-100 text-zinc-500 border-zinc-200',      Icon: X },
};
